import { FC, useEffect, useState, useMemo } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { GridColumns, GridCellParams, DataGrid } from '@material-ui/data-grid';
import {
  Grid,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { observer } from 'mobx-react-lite';
import { orange } from '@material-ui/core/colors';
import DeleteIcon from '@material-ui/icons/Delete';
import { getRowsPerPageOptions } from 'utils/pagination';
import { EndUserCustomForm } from 'components/Forms';
import { EndUserAttributeForm } from 'components/Forms';
export const CustomAttributeTable: FC = observer(() => {
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<any>(null);
  useEffect(() => {
    document.title = 'EasyIdentity: Settings: Customer Attribute';
  }, []);
  const {
    endUserSettingsFormStore: { attributeget, deleteattribute, attributeKey },
    notificationStore: { openNotification },
  } = useStore();

  const attributegetReq = useQuery('attributegetReq', () => attributeget(), {
    onError: () => openNotification('error', `Can't get data`),
  });

  const rows = useMemo(() => {
    return !attributegetReq.isFetching
      ? Array.isArray(attributeKey)
        ? attributeKey.map((attr, index) => ({
            ...attr,
            id: attr.name || index,
          }))
        : []
      : [];
  }, [attributegetReq.isFetching, attributeKey]);
  const deleteattributereq = useMutation((data: any) => deleteattribute(data), {
    mutationKey: 'UserIdGenratereq',
    onSuccess: () => {
      openNotification('success', 'Field deleted successfully!');
      attributegetReq.refetch();
      setOpenConfirmDialog(false);
    },
    onError: () => openNotification('error', `Field deletion failed`),
  });

  const handleDeleteClick = (row: any) => {
    setRowToDelete(row);
    setOpenConfirmDialog(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete) {
      const payload = {
        type: rowToDelete.type,
        label: rowToDelete.label,
      };
      deleteattributereq.mutate(payload);
    }
  };
  const handleOpen = (row: any = null) => {
    setSelectedRow(row);
    setOpenModal(true);
  };
  const handleClose = () => {
    setSelectedRow(null);
    setOpenModal(false);
  };
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setRowToDelete(null);
  };

  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const columns: GridColumns = [
    { field: 'label', headerName: 'Label', flex: 2 },
    { field: 'type', headerName: 'Type', flex: 2 },
    {
      field: 'options',
      headerName: 'ACTIONS',
      sortable: false,
      align: 'center',
      disableColumnMenu: true,
      width: 100,
      renderCell: (params: GridCellParams) => (
        <>
          <IconButton onClick={() => handleOpen(params.row)} size='small'>
            <EditIcon style={{ color: orange[500] }} />
          </IconButton>
          <IconButton size='small' onClick={() => handleDeleteClick(params.row)}>
            <DeleteIcon style={{ fill: '#F44336' }} />
          </IconButton>
        </>
      ),
    },
  ];

  useAppPreloader(attributegetReq.isFetching || deleteattributereq.isLoading);
  return (
    <>
      <EndUserAttributeForm type='general'></EndUserAttributeForm>

      <h1 className={classes.textBox}>
        {' '}
        Add custom attribute fields to capture additional user information. These fields can be used throughout the
        application.
      </h1>
      <div className={classes.leftCol}>
        <Grid item xs={12} className={classes.btnContainer}>
          <Button className={classes.submitBtn} onClick={() => handleOpen(null)} variant='contained'>
            Add Field
          </Button>
        </Grid>
      </div>
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          sortingOrder={['desc', 'asc']}
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <EndUserCustomForm
        open={openModal}
        refetch={() => attributegetReq.refetch()}
        handleClose={handleClose}
        selectedRow={selectedRow}
      />
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete ? This action will permanently remove it from all users and cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color='primary' variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
